import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { CustomsOfficeOfExitForTransitDeclared } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { CUSTOMS_OFFICES_OF_EXIT_FOR_TRANSIT_MULTIPLICITY } from 'shared/ncts/common/constants'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { blankCustomsOfficeOfExitForTransitDeclared } from 'features/declaration/models/blankItems'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormField from 'components/p5/FormField'
import FormFieldArray from 'components/p5/FormFieldArray'
import FormSelect from 'components/p5/FormSelect'

import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'

const scope = 'customsOfficesOfTransitExit'

function CustomsOfficeOfExitForTransit() {
  const { t } = useTranslation()
  const { control } = useFormContext<TransitOperationFormValues>()

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TransitOperationFormValues, CustomsOfficeOfExitForTransitDeclared>({
    control,
    name: scope,
  })

  const [, transitCustomsOfficesOptions] = useCodelist('TRANSIT_EXIT_CUSTOMS_OFFICES')

  const paths = useMemo(
    () => [
      'customsOfficesOfTransitExit',
      ...visibleFields.map((
        f,
      ) => `${scope}.${f.index}.referenceNumber`),
    ],
    [visibleFields],
  )

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', paths)

  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'
  const isAddButtonDisabled = isAddForbidden || visibleFields.length >= CUSTOMS_OFFICES_OF_EXIT_FOR_TRANSIT_MULTIPLICITY

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-offices-of-transit"
        headingLabel={t('declaration.p5.transitExitCustomsOffices')}
        fieldRule={fieldRequirements[scope]}
      >
        {visibleFields.map((fieldWithIndex) => {
          const transitCustomsOffices = fieldWithIndex.field
          const innerItemScope = `${scope}.${fieldWithIndex.index}`

          return (
            <FormFieldArray
              key={`transitCustomsOffice_${transitCustomsOffices.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => softRemove(fieldWithIndex.index)}
              sequenceNumber={transitCustomsOffices.sequenceNumber + 1}
              hidden={transitCustomsOffices.deleted}
            >
              <FormField
                labelKey="declaration.p5.referenceNumber"
                fieldRule={fieldRequirements[`${innerItemScope}.referenceNumber`]}
              >
                <FormSelect
                  field={`${innerItemScope}.referenceNumber`}
                  labelKey="declaration.p5.referenceNumber"
                  options={transitCustomsOfficesOptions}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.referenceNumber`]}
                />
              </FormField>
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(blankCustomsOfficeOfExitForTransitDeclared)}
              disabled={isAddButtonDisabled}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewCustomsOfficeOfExitForTransit')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default CustomsOfficeOfExitForTransit
